import { faInfoCircle } from '@fortawesome/pro-solid-svg-icons';
import {
    setOlbBookingItemService,
    submitOnlineBookingSelectServicesThunk,
    toggleOlbBookingItemAddon
} from 'actions/onlineBooking/OnlineBookingActions';
import { PoweredByCuddles } from 'components/Cuddles/PoweredBy/PoweredByCuddles';
import { RadioCard } from 'components/Form/RadioCard/RadioCard';
import { LeftColumn } from 'components/Layout/TwoColumns/LeftColumn/LeftColumn';
import { RightColumn } from 'components/Layout/TwoColumns/RightColumn/RightColumn';
import { MarketplaceInfo } from 'components/Marketplace/Info/MarketplaceInfo';
import { OnlineBookingResume } from 'components/OnlineBooking/Resume/OnlineBookingResume';
import { PetChip } from 'components/Pet/Chip/PetChip';
import { Alert } from 'components/UI/Alert/Alert';
import { Button } from 'components/UI/Button/Button';
import { Tabs } from 'components/UI/Tabs/Tabs';
import { useMarketplace } from 'hooks/useMarketplace';
import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getActiveAddonServices } from 'reducers/addonServices/AddonServicesSelectors';
import { getOnlineBookingItems } from 'reducers/onlineBooking/OnlineBookingSelectors';
import { getServices } from 'reducers/services/ServicesSelectors';
import { AddonService } from 'types/AddonService';
import { Service } from 'types/Service';
import { getServicePriceByPet, isServiceAvailableForPet } from 'utils/serviceUtils';

import classes from './OnlineBookingSelectServicesStep.module.scss';

enum ServiceTab {
    SERVICES = 'services',
    ADDONS = 'addons'
}

interface PetServicesFormProps {
    selectedPetUuid: string;
    onChange: (petUuid: string) => void;
}

const PetServicesForm: React.FC<PetServicesFormProps> = props => {
    const dispatch = useDispatch();

    const services = useSelector(getServices);
    const addons = useSelector(getActiveAddonServices);
    const bookingItems = useSelector(getOnlineBookingItems);

    const [selectedTab, setSelectedTab] = React.useState<ServiceTab>(ServiceTab.SERVICES);

    const selectedItem = React.useMemo(() => {
        return bookingItems.find(item => item.pet.uuid === props.selectedPetUuid)!;
    }, [bookingItems, props.selectedPetUuid]);

    const servicesList = React.useMemo(() => {
        return services
            .filter(service => isServiceAvailableForPet(service, selectedItem.pet))
            .map(service => {
                const price = getServicePriceByPet(service, selectedItem.pet);

                return {
                    ...service,
                    price: price.toString()
                };
            })
            .filter(service => parseInt(service.price) > 0)
            .sort(function (a, b) {
                if (a.name < b.name) {
                    return -1;
                }
                if (a.name > b.name) {
                    return 1;
                }
                return 0;
            });
        // eslint-disable-next-line
    }, [services, selectedItem]);

    React.useEffect(() => {
        if (!props.selectedPetUuid && bookingItems.length > 0) {
            props.onChange(bookingItems[0].pet.uuid);
        }
    }, [bookingItems, props]);

    React.useEffect(() => {
        setSelectedTab(ServiceTab.SERVICES);
    }, [props.selectedPetUuid]);

    const isAddonSelected = (addon: AddonService) => {
        return selectedItem.addons?.some(a => a.id === addon.id) ?? false;
    };

    const petChangeHandler = (petUuid: string) => {
        props.onChange(petUuid);
    };

    const serviceChangeHandler = (service: Service) => {
        if (addons.length > 0) {
            setSelectedTab(ServiceTab.ADDONS);
        }

        dispatch(setOlbBookingItemService(selectedItem.pet.uuid, service));

        // Keep the pet chip in viewport if it's at the end of the container
        const petChipParent = document.getElementById(selectedItem.pet.uuid)?.parentElement;

        if (
            petChipParent &&
            petChipParent.scrollLeft + petChipParent.clientWidth >= petChipParent.scrollWidth
        ) {
            // This is a hack to ensure the pet chip is in viewport after the service is set
            // TODO: Find a better solution
            setTimeout(() => {
                petChipParent.scrollLeft = petChipParent.scrollWidth - petChipParent.clientWidth;
            }, 1);
        }
    };

    const addonChangeHandler = (addon: AddonService) => {
        const isSelected = isAddonSelected(addon);

        dispatch(toggleOlbBookingItemAddon(selectedItem.pet.uuid, addon, isSelected));
    };

    return (
        <div className={classes.petServicesForm}>
            <h1 className="h1 underlined w-full">Select services</h1>

            <div className="w-full">
                <div className={classes.petTabs}>
                    {bookingItems.map(item => (
                        <PetChip
                            id={item.pet.uuid}
                            key={item.pet.uuid}
                            petName={item.pet.name}
                            petType={item.pet.type}
                            checked={!!item.service?.id}
                            active={props.selectedPetUuid === item.pet.uuid}
                            onClick={() => petChangeHandler(item.pet.uuid)}
                        />
                    ))}
                </div>
            </div>

            <Tabs
                tabs={[
                    {
                        label: 'Services',
                        value: ServiceTab.SERVICES
                    },
                    ...(addons.length > 0
                        ? [
                              {
                                  label: 'Add-ons',
                                  value: ServiceTab.ADDONS
                              }
                          ]
                        : [])
                ]}
                selectedTab={selectedTab}
                className={classes.servicesTabs}
                onChange={setSelectedTab}
            />

            <div className={classes.services}>
                {selectedTab === ServiceTab.SERVICES && (
                    <>
                        {servicesList.length === 0 && (
                            <Alert
                                variant="warning"
                                icon={faInfoCircle}
                                text="There are no services available for your pet. Please contact the business for assistance."
                            />
                        )}

                        {servicesList.map(service => (
                            <RadioCard
                                key={service.id}
                                title={service.name}
                                description={`Starting at $${service.price}`}
                                checked={selectedItem.service?.id === service.id}
                                content={service.description}
                                onChange={() => serviceChangeHandler(service)}
                            />
                        ))}
                    </>
                )}

                {selectedTab === ServiceTab.ADDONS &&
                    addons.map(addon => (
                        <RadioCard
                            key={addon.id}
                            mode="checkbox"
                            title={addon.name}
                            checked={isAddonSelected(addon)}
                            description={`$${addon.price.toFixed(2)}`}
                            onChange={() => addonChangeHandler(addon)}
                        />
                    ))}
            </div>
        </div>
    );
};

export const OnlineBookingSelectServicesStep: React.FC = () => {
    const dispatch = useDispatch();
    const marketplace = useMarketplace();

    const bookingItems = useSelector(getOnlineBookingItems);

    const [selectedPetUuid, setSelectedPetUuid] = React.useState<string>(bookingItems[0].pet.uuid);

    const submitButtonLabel = React.useMemo(() => {
        if (
            bookingItems
                .filter(item => item.pet.uuid !== selectedPetUuid)
                .filter(item => !item.service?.id).length > 0
        ) {
            return 'Next Pet';
        }

        return 'Continue';
    }, [bookingItems, selectedPetUuid]);

    const isValid = React.useMemo(() => {
        const selectedItem = bookingItems.find(item => item.pet.uuid === selectedPetUuid);

        if (selectedItem && !selectedItem.service?.id) {
            return false;
        }

        if (bookingItems.some(item => !item.service?.id)) {
            return true;
        }

        return bookingItems.every(item => item.service !== undefined);
    }, [selectedPetUuid, bookingItems]);

    const submitHandler = () => {
        const nextPetUuid = bookingItems.find(item => !item.service?.id)?.pet.uuid;

        if (nextPetUuid) {
            // Animate the pet chip to center horizontally
            const petChip = document.getElementById(nextPetUuid);
            petChip?.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });

            setSelectedPetUuid(nextPetUuid);

            return;
        }

        dispatch(submitOnlineBookingSelectServicesThunk());
    };

    return (
        <>
            <LeftColumn contentClassName="flex flex-col flex-1 gap-5">
                <PetServicesForm selectedPetUuid={selectedPetUuid} onChange={setSelectedPetUuid} />

                <div className="mt-auto hide-on-mobile-and-tablet pt-1">
                    <PoweredByCuddles />
                </div>
            </LeftColumn>

            <RightColumn
                poweredByCuddles
                footer={
                    <Button fullWidth disabled={!isValid} onClick={submitHandler}>
                        {submitButtonLabel}
                    </Button>
                }
            >
                <div className="flex flex-col gap-5 hide-on-mobile-and-tablet">
                    <MarketplaceInfo inverted marketplace={marketplace} />

                    <OnlineBookingResume />
                </div>
            </RightColumn>
        </>
    );
};
